import React, { useEffect, useMemo, useState } from "react";
import "./CheckoutPage.css";
import Spinner from "../components/Spinner";

const Checkout = () => {
  const [checkoutData, setCheckoutData] = useState(null);

  // lists
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);

  // selected objects { id: string, name: string }
  const [province, setProvince] = useState({ id: "", name: "" });
  const [city, setCity] = useState({ id: "", name: "" });

  const [initialProvince, setInitialProvince] = useState("");
  const [initialCity, setInitialCity] = useState("");

  const [street, setStreet] = useState("");
  const [postalCode, setPostalCode] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [loadingGifts, setLoadingGifts] = useState(false);



  // delivery option state
  const [deliveryOption, setDeliveryOption] = useState("post"); // defaults to post
  const shippingRates = {
    snapp: 0,
    post: 8000,
    express: 35000,
  };

  const token = localStorage.getItem("token");


  useEffect(() => {
    setLoadingGifts(true);
    (async () => {
      try {
        const res = await fetch("https://api.goharesadaf.ir/api/gifts", {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error('Failed to fetch gifts');
        const data = await res.json();
        setGifts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Gifts fetch:", err);
        setGifts([]);
      } finally {
        setLoadingGifts(false);
      }
    })();
  }, []);


  // --- 1) fetch checkout (user + cart)
  useEffect(() => {
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetch("https://api.goharesadaf.ir/api/checkout", {
          headers: {
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || "Failed to fetch checkout data");
        }

        const data = await res.json();
        const user = data.user || {};

        setCheckoutData(data);

        // store raw names (strings) to match later
        setInitialProvince((user.province || "").trim());
        setInitialCity((user.city || "").trim());

        setStreet(user.street || "");
        setPostalCode(user.postal_code || "");
      } catch (err) {
        console.error("Checkout fetch error:", err);
        setError(err.message || "Failed to load checkout");
        setCheckoutData(null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- 2) fetch provinces
  useEffect(() => {
    setLoadingProvinces(true);
    (async () => {
      try {
        const res = await fetch("https://api.goharesadaf.ir/api/locations/states", {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`Provinces fetch failed: ${res.status}`);
        const data = await res.json();
        setProvinces(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch provinces:", err);
        setProvinces([]);
      } finally {
        setLoadingProvinces(false);
      }
    })();
  }, []);

  // --- match initial province string to object
  useEffect(() => {
    if (!provinces.length || !initialProvince) return;
    const want = initialProvince.trim().toLowerCase();
    const matched = provinces.find((p) => (p.name || "").trim().toLowerCase() === want);
    if (matched) {
      setProvince({ id: String(matched.id), name: matched.name });
      setInitialProvince("");
    } else {
      console.warn("Initial province not matched:", initialProvince);
    }
  }, [provinces, initialProvince]);

  // --- fetch cities when province changes
  useEffect(() => {
    (async () => {
      setCities([]);
      setCity({ id: "", name: "" });
      if (!province.id) return;

      setLoadingCities(true);
      try {
        // note: adjust endpoint if your backend uses a different proxy path
        const url = `https://api.goharesadaf.ir/api/locations/cities/${encodeURIComponent(province.id)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`Cities fetch failed: ${res.status}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch cities:", err);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    })();
  }, [province.id]);

  // --- match initial city after cities load
  useEffect(() => {
    if (!cities.length || !initialCity) return;
    const want = initialCity.trim().toLowerCase();
    const matched = cities.find((c) => (c.name || "").trim().toLowerCase() === want);
    if (matched) {
      setCity({ id: String(matched.id), name: matched.name });
      setInitialCity("");
    } else {
      console.warn("Initial city not matched:", initialCity);
    }
  }, [cities, initialCity]);

  // --- determine whether Snapp is available (city === 'تهران')
  const currentCityName = (city.name || initialCity || "").trim().toLowerCase();
  const snappAvailable = currentCityName === "تهران";

  // If snapp isn't available but user had deliveryOption 'snapp', switch to post
  useEffect(() => {
    if (deliveryOption === "snapp" && !snappAvailable) {
      setDeliveryOption("post");
    }
  }, [snappAvailable, deliveryOption]);

  // --- shipping cost calculation
  const subtotal = Number(checkoutData?.summary?.subtotal || 0);
  const shippingCost = useMemo(() => {
    if (deliveryOption && shippingRates[deliveryOption] !== undefined) {
      return shippingRates[deliveryOption];
    }
    // default shipping from backend summary if you prefer to keep it:
    return Number(checkoutData?.summary?.shipping || 0);
  }, [deliveryOption, checkoutData]);

  const total = subtotal + Number(shippingCost || 0);

  const formatToman = (n) =>
    Number(n || 0).toLocaleString("fa-IR");

  // --- Save address (unchanged logic)
  const handleSaveAddress = async () => {
    const sendProvinceName = province.name || initialProvince;
    const sendCityName = city.name || initialCity;

    if (!sendProvinceName || !sendCityName || !street || !postalCode) {
      alert("لطفاً تمام فیلدهای آدرس را کامل کنید.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch("https://api.goharesadaf.ir/api/user/address", {
        method: "PUT",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          province: sendProvinceName,
          city: sendCityName,
          street,
          postal_code: postalCode,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Save failed: ${res.status}`);
      }

      alert("آدرس با موفقیت ذخیره شد.");
      setInitialProvince(sendProvinceName);
      setInitialCity(sendCityName);
    } catch (err) {
      console.error("Save address error:", err);
      alert(err.message || "خطا در ذخیره آدرس");
    } finally {
      setSaving(false);
    }
  };

 const handlePlaceOrder = async () => {
  try {
    // 1) create order (reuse your existing endpoint)
    const createRes = await fetch("https://api.goharesadaf.ir/api/orders", {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        delivery_option: deliveryOption,
        shipping_cost: shippingCost,
        gift_id: selectedGift ? selectedGift.id : null,
      }),
    });

    if (!createRes.ok) {
      const err = await createRes.json().catch(()=>({}));
      alert(err.message || "Error creating order.");
      return;
    }
    const orderData = await createRes.json();
    const orderId = orderData.id;

    // 2) call payment/start to get RefId
    const startRes = await fetch("https://api.goharesadaf.ir/api/payment/start", {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ order_id: orderId }),
    });

    if (!startRes.ok) {
      const err = await startRes.json().catch(()=>({}));
      alert(err.message || "Error starting payment.");
      return;
    }

    const { ref, start_url } = await startRes.json();

    // 3) auto-post the RefId to Mellat
    const form = document.createElement("form");
    form.method = "POST";
    form.action = start_url || "https://bpm.shaparak.ir/pgwchannel/startpay.mellat";
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "RefId";
    input.value = ref;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  } catch (err) {
    console.error(err);
    alert(err.message || "خطا در ثبت سفارش یا پرداخت");
  }
};

{loading && <Spinner />}
{error && <p>خطا در بارگذاری: {error}</p>}

  const cartItems = checkoutData?.cart || [];

  return (
    <div className="checkout-page">
      <h1>تأیید اطلاعات</h1>

      <div className="checkout-top">
        <div className="checkout-left">
          <h2>اطلاعات ارسال</h2>

          <label>استان</label>
          {loadingProvinces ? (
            <select disabled>
              <option>در حال بارگذاری استان‌ها...</option>
            </select>
          ) : (
            <select
              value={province.id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const sel = provinces.find((p) => String(p.id) === String(id));
                if (sel) setProvince({ id: String(sel.id), name: sel.name });
                else setProvince({ id: "", name: "" });
                setCity({ id: "", name: "" });
                setInitialCity("");
              }}
            >
              <option value="">انتخاب استان</option>
              {provinces.map((prov) => (
                <option key={prov.id} value={String(prov.id)}>
                  {prov.name}
                </option>
              ))}
            </select>
          )}
          <label>شهر</label>
          {loadingCities ? (
            <select disabled>
              <option>در حال بارگذاری شهرها...</option>
            </select>
          ) : (
            <select
              value={city.id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const sel = cities.find((c) => String(c.id) === String(id));
                if (sel) setCity({ id: String(sel.id), name: sel.name });
                else setCity({ id: "", name: "" });
              }}
              disabled={!province.id || cities.length === 0}
            >
              <option value="">انتخاب شهر</option>
              {cities.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <label>آدرس (خیابان، کوچه، پلاک و ...)</label>
          <textarea value={street} onChange={(e) => setStreet(e.target.value)} />
          <label>کد پستی</label>
          <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          <button onClick={handleSaveAddress} disabled={saving}>
            {saving ? "در حال ذخیره..." : "تأیید آدرس"}
          </button>
        </div>
        
        {/* GIFT SECTION */}
        {subtotal >= 1000000 ? (
          <div className="gift-section">
            <h3> هدیه رایگان (یک عدد انتخاب کنید):</h3>
            {loadingGifts ? (
              <p>در حال بارگذاری هدایا...</p>
            ) : gifts.length === 0 ? (
              <p className="text-muted">هیچ هدیه‌ای در دسترس نیست.</p>
            ) : (
              <div className="gift-grid">
                {gifts.map((g) => {
                  const isSelected = selectedGift && selectedGift.id === g.id;
                  return (
                    <button
                      type="button"
                      key={g.id}
                      className={`gift-item ${isSelected ? "selected" : ""}`}
                      onClick={() => setSelectedGift(isSelected ? null : g)}
                      aria-pressed={isSelected}
                    >
                      <div className="gift-image-wrap">
                        <img src={g.main_image} alt={g.title} />
                      </div>
                      <div className="gift-body">
                        <div className="gift-title">{g.title}</div>
                        <div className="gift-price">
                          {Number(g.price).toLocaleString("fa-IR")} تومان
                        </div>
                      </div>
                      {isSelected && <div className="gift-check">✔</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="gift-section disabled">
            <h3>🎁 هدیه رایگان</h3>
            <p className="text-muted">
              برای دریافت هدیه رایگان، سفارش شما باید بیشتر از{" "}
              {Number(1000000).toLocaleString("fa-IR")} تومان باشد!
            </p>
          </div>
        )}





      </div>



<div className="checkout-summary-container">
  <h2>خلاصۀ سفارش</h2>

  {cartItems.length === 0 ? (
    <p>سبد خرید شما خالی است!</p>
  ) : (
    <>
      <div className="summary-columns">
        {/* LEFT: selected gift, delivery options, totals */}
        <aside className="summary-left" aria-labelledby="summary-left-heading">
          <h3 id="summary-left-heading" style={{ marginTop: 0 }}>تنظیمات سفارش</h3>

          {/* Selected gift */}
          {selectedGift ? (
            <div className="selected-gift-summary" style={{ marginBottom: 14 }}>
              <strong>هدیه انتخاب شده:</strong>
              <div className="inline-row" style={{ marginTop: 8, alignItems: "center" }}>
                <img
                  src={selectedGift.main_image}
                  alt={selectedGift.title}
                  style={{ width: 56, height: 56, borderRadius: 8, marginLeft: 10 }}
                />
                <div>
                  <div style={{ fontWeight: 700 }}>{selectedGift.title}</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    قیمت: {Number(selectedGift.price).toLocaleString("fa-IR")} تومان (برای شما، رایگان!)
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted" style={{ marginBottom: 12 }}>هدیه‌ای انتخاب نشده است.</div>
          )}

          {/* Delivery options */}
          <div className="delivery-options" style={{ marginBottom: 14 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>روش ارسال</label>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ opacity: snappAvailable ? 1 : 0.5 }}>
                <input
                  type="radio"
                  name="delivery"
                  value="snapp"
                  checked={deliveryOption === "snapp"}
                  onChange={() => setDeliveryOption("snapp")}
                  disabled={!snappAvailable}
                  style={{ marginLeft: 8 }}
                />
                اسنپ (پرداخت در محل)
                {!snappAvailable && <span style={{ marginLeft: 8, color: "#888" }}>— فقط شهر تهران</span>}
              </label>

              <label>
                <input
                  type="radio"
                  name="delivery"
                  value="post"
                  checked={deliveryOption === "post"}
                  onChange={() => setDeliveryOption("post")}
                  style={{ marginLeft: 8 }}
                />
                پست پیشتاز ({formatToman(shippingRates.post)} تومان)
              </label>

              <label>
                <input
                  type="radio"
                  name="delivery"
                  value="express"
                  checked={deliveryOption === "express"}
                  onChange={() => setDeliveryOption("express")}
                  style={{ marginLeft: 8 }}
                />
                تیپاکس ({formatToman(shippingRates.express)} تومان)
              </label>

              {subtotal >= 1000000 && (
                <label>
                  <input
                    type="radio"
                    name="delivery"
                    value="free"
                    checked={deliveryOption === "free"}
                    onChange={() => setDeliveryOption("free")}
                    style={{ marginLeft: 8 }}
                  />
                  ارسال رایگان (ویژه سفارش‌های بالای یک میلیون تومان)
                </label>
              )}
            </div>
          </div>


          {/* Totals */}
          <div className="checkout-totals" style={{ borderTop: "1px solid #eee", paddingTop: 12 }}>
            <p style={{ margin: "6px 0" }}>جمع کل: {formatToman(subtotal)} تومان</p>
            <p style={{ margin: "6px 0" }}>هزینه ارسال: {formatToman(shippingCost)} تومان</p>
            <h3 style={{ marginTop: 10 }}>قابل پرداخت: {formatToman(total)} تومان</h3>

      {/* Button row spans full width */}
      <div className="checkout-button-row" style={{ marginTop: 18 }}>
        <button className="place-order-btn" onClick={handlePlaceOrder} aria-label="Place order">
          ثبت نهایی و انتقال به درگاه پرداخت
        </button>
      </div>

          </div>
        </aside>

        {/* RIGHT: products list */}
        <section className="summary-right" aria-labelledby="summary-right-heading">
          <h3 id="summary-right-heading" style={{ marginTop: 0 }}>محصولات سفارش</h3>

          <ul className="checkout-items" style={{ marginTop: 8 }}>
            {cartItems.map((item) => (
              <li key={item.id} className="checkout-item" style={{ alignItems: "flex-start" }}>
                <img src={item.image} alt={item.title} />
                <div>
                  <p style={{ margin: 0, fontWeight: 700 }}>{item.title}</p>
                  <span style={{ color: "#6b7280" }}>
                    {Number(item.quantity).toLocaleString("fa-IR")} ×{" "}
                    {Number(item.price).toLocaleString("fa-IR")} تومان ={" "}
                    {Number(item.subtotal).toLocaleString("fa-IR")} تومان
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  )}
</div>




    </div>
  );
};

export default Checkout;
